import React, { useEffect, useMemo, useState } from "react";
import {
  BookOpen, HelpCircle,
  Pencil, Trash2, Plus, ArrowUp, ArrowDown, X, Check, Upload, Download, Search, LifeBuoy, Loader2,
} from "lucide-react";
import { useAuth, canManageContent } from "../contexts/AuthContext";
import ConfirmDialog from "../components/ConfirmDialog";
import { authFetch } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Format an ISO datetime as a compact "MMM D, YYYY".
function formatEditedDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric", year: "numeric" });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

// 2026-02-25 (Dave) — <Highlight> wraps every match of `patterns` (an
// array of raw strings, case-insensitive) in a coral-tinted <mark> so
// the searcher's eye jumps straight to the hit. Longest patterns are
// matched first to avoid partial-word truncation (e.g., "assessment"
// before "assess"). Returns the original text unchanged when there are
// no patterns.
function Highlight({ text, patterns }) {
  if (!text || !patterns || patterns.length === 0) return text;
  const uniq = Array.from(new Set(patterns.filter(Boolean)));
  uniq.sort((a, b) => b.length - a.length);
  const escaped = uniq.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts = String(text).split(re);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark
        key={i}
        style={{ background: "#FEE7DF", color: "#B54427", padding: "0 1px", borderRadius: 2 }}
      >
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
}

// Individual FAQ / Glossary row — head always visible, body always visible.
// 2026-02-16 (Dave) — was `ExpandableItem` with a chevron-toggled body;
// Dave asked that answers/definitions no longer "pop up" but stay
// always visible so a reader can scan the whole set in one pass.
function ExpandableItem({ head, body, updatedAt, testid, admin, onEdit, onDelete, onUp, onDown, isFirst, isLast, highlight }) {
  const editedLabel = formatEditedDate(updatedAt);
  return (
    <div className="border-b border-rule last:border-b-0 py-2" data-testid={testid}>
      <div className="flex items-start gap-2">
        <div className="flex items-start gap-2 flex-1">
          <span className="text-[14px] text-ink font-semibold">
            <Highlight text={head} patterns={highlight} />
          </span>
        </div>
        {admin && (
          <div className="flex items-center gap-0.5 shrink-0">
            <IconBtn title="Move up" onClick={onUp} disabled={isFirst} testid={`${testid}-up`}>
              <ArrowUp size={12} />
            </IconBtn>
            <IconBtn title="Move down" onClick={onDown} disabled={isLast} testid={`${testid}-down`}>
              <ArrowDown size={12} />
            </IconBtn>
            <IconBtn title="Edit" onClick={onEdit} testid={`${testid}-edit`}>
              <Pencil size={12} />
            </IconBtn>
            <IconBtn title="Delete" onClick={onDelete} testid={`${testid}-delete`} danger>
              <Trash2 size={12} />
            </IconBtn>
          </div>
        )}
      </div>
      <div className="mt-1.5 text-[13.5px] text-slate leading-relaxed whitespace-pre-wrap">
        <Highlight text={body} patterns={highlight} />
        {editedLabel && (
          <div
            className="mt-2 text-[10.5px] tracking-[0.05em] uppercase text-slate/70 not-italic font-normal"
            data-testid={`${testid}-edited`}
          >
            Last edited {editedLabel}
          </div>
        )}
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, disabled, title, testid, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`p-1 rounded-[3px] transition-colors ${danger ? "text-coral hover:bg-coral/10" : "text-slate hover:bg-navy/10 hover:text-navy"} disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

// Modal for add/edit of a FAQ or Glossary entry.
function EditorModal({ kind, initial, onClose, onSave, testid }) {
  const isFAQ = kind === "faq";
  const [head, setHead] = useState(initial ? (isFAQ ? initial.question : initial.term) : "");
  const [body, setBody] = useState(initial ? (isFAQ ? initial.answer : initial.definition) : "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!head.trim() || !body.trim()) {
      setErr("Both fields are required.");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        [isFAQ ? "question" : "term"]: head.trim(),
        [isFAQ ? "answer" : "definition"]: body.trim(),
      });
      onClose();
    } catch (e) {
      setErr(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="bg-white border-2 border-navy rounded-[8px] shadow-2xl w-[min(560px,92vw)] max-h-[86vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-rule bg-navy-wash">
          <div className="text-[12px] tracking-[0.14em] uppercase font-bold text-navy">
            {initial ? "Edit" : "New"} {isFAQ ? "FAQ" : "Glossary Term"}
          </div>
          <button onClick={onClose} className="text-slate hover:text-navy" data-testid={`${testid}-close`}>
            <X size={16} />
          </button>
        </div>
        <div className="p-4 flex flex-col gap-3 overflow-auto">
          <label className="flex flex-col gap-1">
            <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
              {isFAQ ? "Question" : "Term"}
            </span>
            <input
              type="text"
              value={head}
              onChange={(e) => setHead(e.target.value)}
              className="border border-rule rounded-[3px] px-2 py-1.5 text-[13px] focus:outline-none focus:border-navy"
              data-testid={`${testid}-head`}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
              {isFAQ ? "Answer" : "Definition"}
            </span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={7}
              className="border border-rule rounded-[3px] px-2 py-1.5 text-[12.5px] leading-relaxed focus:outline-none focus:border-navy resize-y"
              data-testid={`${testid}-body`}
            />
          </label>
          {err && <div className="text-[11.5px] text-coral">{err}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-rule bg-navy-wash">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-[12px] text-slate hover:text-navy"
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-3 py-1.5 text-[12px] bg-navy text-white rounded-[3px] hover:bg-navy/90 disabled:opacity-50 flex items-center gap-1.5"
            data-testid={`${testid}-save`}
          >
            <Check size={13} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminHeaderBtn({ onClick, testid, children }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="flex items-center gap-1 px-2 py-0.5 text-[11px] text-navy border border-navy rounded-[3px] hover:bg-navy hover:text-white transition-colors"
      data-testid={testid}
    >
      {children}
    </button>
  );
}

// Always-expanded panel used for FAQ + Glossary. Header stays fixed while
// the item list scrolls independently.
function ContentPanel({ icon: Icon, title, count, testid, headerRight, children }) {
  return (
    <div
      className="border border-rule bg-white flex flex-col min-h-0 rounded-[3px] overflow-hidden"
      data-testid={testid}
    >
      {/* 2026-02-16 (Dave) — dark navy header with white text/icon to
          match the Contributions + Elements tab column-header
          treatment. Housekeeping backlog: canonical table-header
          class (PRE_PRODUCTION_CLEANUP §16). `headerRight` buttons
          intentionally keep their own styling per Dave. */}
      <div
        className="flex items-center gap-3 px-4 py-3 border-b border-rule shrink-0"
        style={{ background: "#1C4E80" }}
      >
        <Icon size={18} className="shrink-0" style={{ color: "#fff" }} />
        <div className="flex-1">
          <div className="text-[14.5px] font-semibold" style={{ color: "#fff" }}>
            {title}
            {typeof count === "number" && (
              <span
                className="ml-2 text-[11px] font-normal"
                style={{ color: "rgba(255,255,255,0.72)" }}
              >
                ({count})
              </span>
            )}
          </div>
        </div>
        {headerRight && (
          <div className="flex items-center gap-1 shrink-0">{headerRight}</div>
        )}
      </div>
      <div
        className="px-4 py-2 overflow-y-auto flex-1 min-h-0"
        data-testid={`${testid}-body`}
      >
        {children}
      </div>
    </div>
  );
}

export default function SupportScreen() {
  const [faqs, setFaqs] = useState([]);
  const [glossary, setGlossary] = useState([]);
  const [loading, setLoading] = useState(true);
  // 2026-02-25 (Dave) — client-side keyword search across BOTH FAQ and
  // Glossary content. Question / answer / term / definition are the
  // "meta tags" — no separate tag store to maintain, so results are
  // always in sync with the admin-edited content.
  const [query, setQuery] = useState("");
  // 2026-02-25 (Dave) — Technical support ticket modal.
  const [supportOpen, setSupportOpen] = useState(false);

  const { user } = useAuth();
  const admin = canManageContent(user);

  // { kind: "faq"|"glossary", initial: row|null } | null
  const [editor, setEditor] = useState(null);

  // 2026-02-16 (Dave) — replace native window.confirm/alert with branded ConfirmDialog.
  // deleteConfirm = { kind, id, label } | null — destructive confirm before delete.
  // alertDialog = { title, message, tone } | null — one-button ack dialog for
  //   export/import success and error surfaces.
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [alertDialog, setAlertDialog] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/support/faqs`, { credentials: "include" }).then((r) => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API}/api/support/glossary`, { credentials: "include" }).then((r) => r.ok ? r.json() : []).catch(() => []),
    ]).then(([f, g]) => {
      setFaqs(Array.isArray(f) ? f : []);
      setGlossary(Array.isArray(g) ? g : []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, [user?.user_id]);

  const saveFAQ = async (payload) => {
    const initial = editor?.initial;
    const url = initial
      ? `${API}/api/support/faqs/${initial.id}`
      : `${API}/api/support/faqs`;
    const res = await fetch(url, {
      method: initial ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `HTTP ${res.status}`);
    }
    load();
  };

  const saveGlossary = async (payload) => {
    const initial = editor?.initial;
    const url = initial
      ? `${API}/api/support/glossary/${initial.id}`
      : `${API}/api/support/glossary`;
    const res = await fetch(url, {
      method: initial ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const t = await res.text();
      try {
        const j = JSON.parse(t);
        throw new Error(j.detail || `HTTP ${res.status}`);
      } catch {
        throw new Error(t || `HTTP ${res.status}`);
      }
    }
    load();
  };

  const deleteRow = (kind, id, label) => {
    setDeleteConfirm({ kind, id, label });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const { kind, id } = deleteConfirm;
    const path = kind === "faq" ? "faqs" : "glossary";
    const res = await fetch(`${API}/api/support/${path}/${id}`, { method: "DELETE" });
    if (!res.ok) {
      throw new Error(`Delete failed (HTTP ${res.status}).`);
    }
    setDeleteConfirm(null);
    load();
  };

  const swap = async (kind, list, idx, dir) => {
    const other = idx + dir;
    if (other < 0 || other >= list.length) return;
    const a = list[idx];
    const b = list[other];
    const path = kind === "faq" ? "faqs" : "glossary";
    const res = await fetch(`${API}/api/support/${path}/reorder`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [
          { id: a.id, order_idx: b.order_idx },
          { id: b.id, order_idx: a.order_idx },
        ],
      }),
    });
    if (res.ok) load();
  };

  const exportCsv = async (kind) => {
    const path = kind === "faq" ? "faqs" : "glossary";
    const res = await fetch(`${API}/api/support/${path}/export`, { credentials: "include" });
    if (!res.ok) {
      setAlertDialog({
        title: "Export failed",
        message: `Export failed (${res.status}). ${await res.text()}`,
        tone: "danger",
      });
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = kind === "faq" ? "ureserve_faqs.csv" : "ureserve_glossary.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importCsv = async (kind, file) => {
    if (!file) return;
    const path = kind === "faq" ? "faqs" : "glossary";
    const kindLabel = kind === "faq" ? "FAQ" : "Glossary";
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${API}/api/support/${path}/import`, {
      method: "POST",
      body: form,
      credentials: "include",
    });
    const text = await res.text();
    let body = null;
    try { body = JSON.parse(text); } catch { /* not json */ }
    if (!res.ok) {
      const msg = (body && body.detail) || text || `HTTP ${res.status}`;
      setAlertDialog({
        title: `${kindLabel} import failed`,
        message: msg,
        tone: "danger",
      });
      return;
    }
    const skippedLines = (body.skipped || []).map((s) => `  · Row ${s.row}: ${s.reason}`).join("\n");
    const summary =
      `Rows in file: ${body.total_processed}\n` +
      `Added: ${body.added}\n` +
      `Updated: ${body.updated}\n` +
      `Skipped: ${(body.skipped || []).length}` +
      (skippedLines ? `\n${skippedLines}` : "");
    setAlertDialog({
      title: `${kindLabel} import complete`,
      message: summary,
      tone: "neutral",
    });
    load();
  };

  const faqImportRef = React.useRef(null);
  const glossaryImportRef = React.useRef(null);

  // 2026-02-25 (Dave) — Search across question / answer / term /
  // definition. Case-insensitive substring match. Multi-word queries
  // AND the words together (all must appear somewhere in the row).
  //
  // Synonym boost: each user-typed word is expanded to its equivalence
  // cluster so someone typing "HOA" finds rows using "association" and
  // vice-versa. Clusters are single-word and case-insensitive; add new
  // rows as needed. Multi-word phrases (e.g., "special assessment")
  // still work — they'll just search on their component words.
  const SYNONYM_CLUSTERS = useMemo(() => [
    ["hoa", "coa", "association", "community"],
    ["assessment", "contribution"],
    ["element", "component"],
    ["fund", "reserve"],
    ["waterfall", "chart", "projection"],
    ["board", "director", "trustee"],
    ["owner", "member", "resident"],
    ["firm", "tenant"],
    ["plan", "study"],
    ["percent", "percentage", "%"],
    ["shortfall", "deficit"],
  ], []);
  const synonymIndex = useMemo(() => {
    const map = new Map();
    for (const cluster of SYNONYM_CLUSTERS) {
      const set = new Set(cluster.map((w) => w.toLowerCase()));
      for (const word of cluster) map.set(word.toLowerCase(), set);
    }
    return map;
  }, [SYNONYM_CLUSTERS]);

  const tokens = useMemo(
    () => query.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [query],
  );
  // Each token → group of alternatives (itself + synonyms).
  const tokenGroups = useMemo(
    () => tokens.map((t) => {
      const cluster = synonymIndex.get(t);
      return cluster ? Array.from(cluster) : [t];
    }),
    [tokens, synonymIndex],
  );
  // Flat list of every alternative to feed into <Highlight>.
  const highlightPatterns = useMemo(
    () => Array.from(new Set(tokenGroups.flat())),
    [tokenGroups],
  );

  const rowMatches = (fields) => {
    if (tokenGroups.length === 0) return true;
    const hay = fields.filter(Boolean).join(" \n ").toLowerCase();
    return tokenGroups.every((group) => group.some((alt) => hay.includes(alt)));
  };
  const filteredFaqs = useMemo(
    () => faqs.filter((f) => rowMatches([f.question, f.answer])),
    [faqs, tokenGroups],
  );
  const filteredGlossary = useMemo(
    () => glossary.filter((g) => rowMatches([g.term, g.definition])),
    [glossary, tokenGroups],
  );
  const searching = tokenGroups.length > 0;
  const totalHits = filteredFaqs.length + filteredGlossary.length;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden px-4 pb-4" data-testid="screen-support">
      <div
        className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-3 py-2 border border-rule bg-navy-wash rounded-[3px]"
        data-testid="support-guides-strip"
      >
        <div className="text-[13px] tracking-[0.14em] uppercase font-bold text-navy justify-self-start" data-testid="support-title">
          Support & Help
        </div>
        <div className="contents">
        <div
          className="flex items-center gap-1.5 border border-navy bg-white rounded-[3px] px-2 py-1"
          style={{ width: 380 }}
          data-testid="support-search"
        >
          <Search size={12} className="text-slate shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search FAQs and Glossary…"
            className="flex-1 min-w-0 text-[11.5px] bg-transparent focus:outline-none placeholder:text-slate/70"
            data-testid="support-search-input"
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              title="Clear search"
              className="text-slate hover:text-coral shrink-0"
              data-testid="support-search-clear"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* Right column — Technical Support pill. Navy border + text
            matching the New Plan Year guide button for a consistent
            "action pill" look across the strip. */}
        <button
          type="button"
          onClick={() => setSupportOpen(true)}
          data-testid="support-open-ticket"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-navy text-navy text-[11px] font-semibold rounded-[3px] hover:bg-navy hover:text-white transition-colors justify-self-end"
          title="Send a technical support request to the uReserve team"
        >
          <LifeBuoy size={12} />
          For Technical Support
        </button>
        </div>
      </div>

      {searching && (
        <div
          className="mt-2 text-[11px] text-slate"
          data-testid="support-search-summary"
        >
          {totalHits === 0 ? (
            <span className="text-coral">
              No matches for “{query.trim()}”. Try a shorter or different word.
            </span>
          ) : (
            <>
              <span className="font-semibold text-navy">{totalHits}</span>{" "}
              match{totalHits === 1 ? "" : "es"} for “
              <span className="font-semibold text-navy">{query.trim()}</span>”
              ({filteredFaqs.length} FAQ{filteredFaqs.length === 1 ? "" : "s"},{" "}
              {filteredGlossary.length} Glossary term
              {filteredGlossary.length === 1 ? "" : "s"})
            </>
          )}
        </div>
      )}

      {/* Two half-width columns with a 24px gutter. Each column owns its own
          scrollbar so long lists never push the page height. */}
      <div style={{ borderTop: "2px solid #EA6A47", margin: "6px 0" }} data-testid="support-coral-rule" />
      <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
        <ContentPanel
          icon={HelpCircle}
          title="Frequently Asked Questions"
          count={loading ? undefined : (searching ? filteredFaqs.length : faqs.length)}
          testid="support-faqs"
          headerRight={admin && (
            <>
              <input
                ref={faqImportRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) importCsv("faq", f);
                }}
                data-testid="support-faqs-import-input"
              />
              <AdminHeaderBtn onClick={() => exportCsv("faq")} testid="support-faqs-export">
                <Download size={12} /> Export
              </AdminHeaderBtn>
              <AdminHeaderBtn onClick={() => faqImportRef.current?.click()} testid="support-faqs-import">
                <Upload size={12} /> Import
              </AdminHeaderBtn>
              <AdminHeaderBtn onClick={() => setEditor({ kind: "faq", initial: null })} testid="support-faqs-add">
                <Plus size={12} /> New FAQ
              </AdminHeaderBtn>
            </>
          )}
        >
          {loading && <div className="text-[12px] text-slate italic">Loading…</div>}
          {!loading && faqs.length === 0 && (
            <div className="text-[12px] text-slate italic">No FAQs yet.</div>
          )}
          {!loading && faqs.length > 0 && searching && filteredFaqs.length === 0 && (
            <div className="text-[12px] text-slate italic" data-testid="support-faqs-nomatch">
              No FAQ matches “{query.trim()}”.
            </div>
          )}
          {!loading && filteredFaqs.map((f, i) => (
            <ExpandableItem
              key={f.id}
              head={f.question}
              body={f.answer}
              updatedAt={f.updated_at}
              testid={`faq-${f.id}`}
              admin={admin && !searching}
              onEdit={() => setEditor({ kind: "faq", initial: f })}
              onDelete={() => deleteRow("faq", f.id, f.question)}
              onUp={() => swap("faq", faqs, faqs.indexOf(f), -1)}
              onDown={() => swap("faq", faqs, faqs.indexOf(f), +1)}
              isFirst={i === 0}
              isLast={i === filteredFaqs.length - 1}
              highlight={searching ? highlightPatterns : null}
            />
          ))}
        </ContentPanel>

        <ContentPanel
          icon={BookOpen}
          title="Glossary"
          count={loading ? undefined : (searching ? filteredGlossary.length : glossary.length)}
          testid="support-glossary"
          headerRight={admin && (
            <>
              <input
                ref={glossaryImportRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) importCsv("glossary", f);
                }}
                data-testid="support-glossary-import-input"
              />
              <AdminHeaderBtn onClick={() => exportCsv("glossary")} testid="support-glossary-export">
                <Download size={12} /> Export
              </AdminHeaderBtn>
              <AdminHeaderBtn onClick={() => glossaryImportRef.current?.click()} testid="support-glossary-import">
                <Upload size={12} /> Import
              </AdminHeaderBtn>
              <AdminHeaderBtn onClick={() => setEditor({ kind: "glossary", initial: null })} testid="support-glossary-add">
                <Plus size={12} /> New Term
              </AdminHeaderBtn>
            </>
          )}
        >
          {loading && <div className="text-[12px] text-slate italic">Loading…</div>}
          {!loading && glossary.length === 0 && (
            <div className="text-[12px] text-slate italic">No glossary entries yet.</div>
          )}
          {!loading && glossary.length > 0 && searching && filteredGlossary.length === 0 && (
            <div className="text-[12px] text-slate italic" data-testid="support-glossary-nomatch">
              No glossary term matches “{query.trim()}”.
            </div>
          )}
          {!loading && filteredGlossary.map((g, i) => (
            <ExpandableItem
              key={g.id}
              head={g.term}
              body={g.definition}
              updatedAt={g.updated_at}
              testid={`glossary-${g.id}`}
              admin={admin && !searching}
              onEdit={() => setEditor({ kind: "glossary", initial: g })}
              onDelete={() => deleteRow("glossary", g.id, g.term)}
              onUp={() => swap("glossary", glossary, glossary.indexOf(g), -1)}
              onDown={() => swap("glossary", glossary, glossary.indexOf(g), +1)}
              isFirst={i === 0}
              isLast={i === filteredGlossary.length - 1}
              highlight={searching ? highlightPatterns : null}
            />
          ))}
        </ContentPanel>
      </div>

      {editor && (
        <EditorModal
          kind={editor.kind}
          initial={editor.initial}
          onClose={() => setEditor(null)}
          onSave={editor.kind === "faq" ? saveFAQ : saveGlossary}
          testid={editor.kind === "faq" ? "faq-editor" : "glossary-editor"}
        />
      )}

      {/* 2026-02-16 (Dave) — branded replacements for window.confirm() /
          window.alert() on the Support tab. See ConfirmDialog for
          keyboard + async semantics. */}
      <ConfirmDialog
        open={!!deleteConfirm}
        title={
          deleteConfirm?.kind === "faq"
            ? "Delete this FAQ?"
            : "Delete this glossary term?"
        }
        message={
          deleteConfirm ? (
            <>
              You&apos;re about to delete{" "}
              <span className="font-semibold">
                &ldquo;{deleteConfirm.label}&rdquo;
              </span>
              . This cannot be undone.
            </>
          ) : ""
        }
        confirmLabel="Delete"
        tone="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
        testid="support-delete-confirm"
      />

      <ConfirmDialog
        open={!!alertDialog}
        title={alertDialog?.title || ""}
        message={
          alertDialog?.message ? (
            <pre className="text-[12px] font-mono whitespace-pre-wrap leading-relaxed">
              {alertDialog.message}
            </pre>
          ) : ""
        }
        confirmLabel="OK"
        tone={alertDialog?.tone || "neutral"}
        hideCancel
        onConfirm={() => setAlertDialog(null)}
        onCancel={() => setAlertDialog(null)}
        testid="support-alert-dialog"
      />

      {supportOpen && (
        <TechnicalSupportModal onClose={() => setSupportOpen(false)} />
      )}
    </div>
  );
}



// 2026-02-25 (Dave) — Technical Support ticket modal. User describes
// the problem (required) + optional error details. On send the backend
// dispatches TWO Postmark emails: one to support@ureserveplanning.com
// with ReplyTo = requester (so a rep can reply straight from Gmail),
// and one acknowledgment back to the requester.
function TechnicalSupportModal({ onClose }) {
  const [description, setDescription] = useState("");
  const [errorDetails, setErrorDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentAt, setSentAt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const canSend = !busy && description.trim().length > 0 && !sentAt;

  const send = async () => {
    setBusy(true); setErr("");
    try {
      const res = await authFetch(`${API}/api/support/technical`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          description: description.trim(),
          error_details: errorDetails.trim() || null,
        }),
      });
      if (!res.ok) {
        let msg = `Send failed (${res.status})`;
        try { const j = await res.json(); if (j?.detail) msg = j.detail; } catch { /* ignore */ }
        throw new Error(msg);
      }
      setSentAt(new Date());
    } catch (e) {
      setErr(e?.message || "Send failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.42)" }}
      onClick={busy ? undefined : onClose}
      data-testid="tech-support-modal"
    >
      <div
        className="bg-white rounded-[4px] shadow-2xl border border-rule w-[540px] max-w-[92vw] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-rule">
          <div className="flex items-center gap-2">
            <LifeBuoy size={14} className="text-coral" />
            <div className="text-[13px] font-bold text-navy tracking-wide">Technical Support Request</div>
          </div>
          <button
            type="button"
            onClick={busy ? undefined : onClose}
            className="text-slate hover:text-coral"
            data-testid="tech-support-close"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-4 py-3">
          {sentAt ? (
            <div className="flex flex-col items-center text-center py-6" data-testid="tech-support-sent">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
                style={{ background: "#EBF6EE" }}
              >
                <Check size={20} className="text-[#1F7A3A]" />
              </div>
              <div className="text-[14px] font-semibold text-navy mb-1">Your request has been sent.</div>
              <div className="text-[12px] text-slate max-w-[420px] leading-snug">
                A confirmation email is on its way to your inbox and a member of the uReserve support team will reply as soon as possible.
              </div>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 px-4 py-1.5 text-[12px] font-semibold text-white rounded-[3px]"
                style={{ background: "#1C4E80" }}
                data-testid="tech-support-close-2"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="text-[12px] text-slate mb-3 leading-snug">
                Please describe the problem you&apos;re running into. If you have any error messages you saw on-screen, paste them into the second box — details help us track things down faster.
              </div>

              <label className="block text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate mb-1">
                Problem description <span className="text-coral">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={busy}
                rows={5}
                placeholder="What happened? What were you trying to do?"
                className="w-full border border-rule rounded-[3px] px-2 py-1.5 text-[12px] focus:outline-none focus:border-navy mb-3"
                data-testid="tech-support-description"
                autoFocus
              />

              <label className="block text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate mb-1">
                Error messages / additional detail (optional)
              </label>
              <textarea
                value={errorDetails}
                onChange={(e) => setErrorDetails(e.target.value)}
                disabled={busy}
                rows={3}
                placeholder="Paste any red error banners, popup text, or console output here."
                className="w-full border border-rule rounded-[3px] px-2 py-1.5 text-[12px] font-mono focus:outline-none focus:border-navy"
                data-testid="tech-support-error-details"
              />

              {err && (
                <div
                  className="mt-2 inline-flex items-start gap-1.5 px-2 py-1 rounded-[3px] text-[11px] leading-snug text-coral bg-[#FEF5F2] border border-coral"
                  data-testid="tech-support-error"
                >
                  <span>{err}</span>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="px-3 py-1.5 text-[12px] font-semibold text-slate hover:text-navy rounded-[3px]"
                  data-testid="tech-support-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={send}
                  disabled={!canSend}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-[3px] transition-colors"
                  style={{
                    background: canSend ? "#EA6A47" : "#B4BEC7",
                    color: "#fff",
                    cursor: canSend ? "pointer" : "not-allowed",
                  }}
                  data-testid="tech-support-send"
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <LifeBuoy size={12} />}
                  {busy ? "Sending…" : "Send Support Request"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

